import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Paper,
  Divider,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Fade,
  Slide,
  IconButton,
  Chip,
  Stack,
  Alert,
  Collapse,
  Avatar,
  ButtonGroup,
  Button,
  LinearProgress,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  VolumeUp as VolumeIcon,
  People as PeopleIcon,
  SportsEsports as GameIcon,
  Check as CheckIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useUser } from "../authMethods/UserContext";
import ChessButton from "./ChessButton";
import { useTranslation } from "react-i18next";
import { sendMessage } from "../services/websocketManager";
import { generalMessageTemplate } from "../entities/generalMessageTemplate";
import { useNavigate } from "react-router-dom";

const SettingsPage = () => {
  const { userData } = useUser();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const initialSettings = useMemo(() => ({
    allowToBeAddedToFavorites: userData?.settings?.allowToBeAddedToFavorites ?? true,
    allowOthersToSeeMyProfile: userData?.settings?.allowOthersToSeeMyProfile ?? true,
    allowOthersToInviteToGame: userData?.settings?.allowOthersToInviteToGame ?? true,
    enableVolume: userData?.settings?.enableVolume ?? true,
  }), [userData?.settings]);

  const [settings, setSettings] = useState({ ...initialSettings });

  const handleToggleChange = (field) => (event) => {
    const newValue = event.target.checked;
    setSettings((prev) => ({ ...prev, [field]: newValue }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    const message = { ...generalMessageTemplate };
    message.messageType = "update_settings";
    message.settings = { ...settings };
    sendMessage(message);
    
    // Simulate save delay for better UX
    setTimeout(() => {
      setSaving(false);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    }, 1500);
  };

  const handleLeaveWithoutSaving = () => {
    navigate(-1);
  };

  const isUnchanged = useMemo(() => {
    return (
      settings.allowToBeAddedToFavorites === initialSettings.allowToBeAddedToFavorites &&
      settings.allowOthersToSeeMyProfile === initialSettings.allowOthersToSeeMyProfile &&
      settings.allowOthersToInviteToGame === initialSettings.allowOthersToInviteToGame &&
      settings.enableVolume === initialSettings.enableVolume
    );
  }, [settings, initialSettings]);

  const settingsConfig = [
    {
      label: t("Allow other players to add me to their favorites"),
      description: t("Others can save you to their friends list"),
      field: "allowToBeAddedToFavorites",
      icon: <PeopleIcon />,
      color: "#e91e63",
    },
    {
      label: t("Allow others to see my profile"),
      description: t("Your profile information will be visible to others"),
      field: "allowOthersToSeeMyProfile",
      icon: <VisibilityIcon />,
      color: "#2196f3",
    },
    {
      label: t("Allow others to invite me to a game"),
      description: t("Other players can send you game invitations"),
      field: "allowOthersToInviteToGame",
      icon: <GameIcon />,
      color: "#4caf50",
    },
    {
      label: t("Enable sound"),
      description: t("Play sounds for game events and notifications"),
      field: "enableVolume",
      icon: <VolumeIcon />,
      color: "#ff9800",
    },
  ];

  return (
    <Box
      sx={{
        marginTop: "10px",
        minHeight: "90vh",
        background: "linear-gradient(135deg, #4a707d 0%, #4a707d 100%)",
        p: isMobile ? 1 : 2,
        display: "flex",
        flexDirection: "column",
        border: "2px solid #000000",
          borderRadius: 3,
          boxShadow: 4,
      }}
    >
      {/* Header */}
      <Slide direction="down" in timeout={500}>
        <Paper
          elevation={8}
          sx={{
            mb: 2,
            borderRadius: 4,
            background: "linear-gradient(45deg, #1a1a1a 30%, #2d2d2d 90%)",
            color: "white",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: "linear-gradient(90deg, #e91e63, #2196f3, #4caf50, #ff9800)",
            }}
          />
          
          <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              onClick={handleLeaveWithoutSaving}
              sx={{
                color: "white",
                backgroundColor: "rgba(255,255,255,0.1)",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.2)" },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            
            <Avatar
              sx={{
                backgroundColor: "primary.main",
                width: 40,
                height: 40,
              }}
            >
              <SettingsIcon />
            </Avatar>
            
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" fontWeight="bold">
                {t("Settings")}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                {t("Customize your experience")}
              </Typography>
            </Box>

            {!isUnchanged && (
              <Fade in>
                <Chip
                  label={t("Unsaved changes")}
                  color="warning"
                  size="small"
                  sx={{ fontSize: "0.75rem" }}
                />
              </Fade>
            )}
          </Box>
        </Paper>
      </Slide>

      {/* Success Alert */}
      <Collapse in={showSuccess}>
        <Alert
          severity="success"
          icon={<CheckIcon />}
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setShowSuccess(false)}
        >
          {t("Settings saved successfully!")}
        </Alert>
      </Collapse>

      {/* Settings Cards */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <Stack spacing={2}>
          {settingsConfig.map((config, index) => (
            <Fade
              key={config.field}
              in
              timeout={600 + index * 200}
            >
              <Card
                elevation={4}
                sx={{
                  borderRadius: 3,
                  overflow: "hidden",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 8px 25px ${config.color}20`,
                  },
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                    <Avatar
                      sx={{
                        backgroundColor: `${config.color}15`,
                        color: config.color,
                        width: 48,
                        height: 48,
                      }}
                    >
                      {config.icon}
                    </Avatar>
                    
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        sx={{ mb: 0.5 }}
                      >
                        {config.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "0.85rem", lineHeight: 1.4 }}
                      >
                        {config.description}
                      </Typography>
                    </Box>
                    
                    <Switch
                      checked={settings[config.field]}
                      onChange={handleToggleChange(config.field)}
                      sx={{
                        "& .MuiSwitch-thumb": {
                          backgroundColor: settings[config.field] ? config.color : "grey.400",
                        },
                        "& .MuiSwitch-track": {
                          backgroundColor: settings[config.field] ? `${config.color}40` : "grey.300",
                        },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Fade>
          ))}
        </Stack>
      </Box>

      {/* Info Card */}
      <Fade in timeout={1200}>
        <Card
          sx={{
            mt: 2,
            borderRadius: 3,
            backgroundColor: "info.main",
            color: "info.contrastText",
          }}
        >
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <InfoIcon fontSize="small" />
              <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                {t("Changes are saved automatically and apply immediately")}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Fade>

      {/* Action Buttons */}
      <Slide direction="up" in timeout={800}>
        <Paper
          elevation={8}
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 4,
            background: "linear-gradient(45deg, #ffffff 30%, #f5f5f5 90%)",
          }}
        >
          {saving && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress
                sx={{
                  borderRadius: 2,
                  height: 6,
                  "& .MuiLinearProgress-bar": {
                    background: "linear-gradient(90deg, #e91e63, #2196f3)",
                  },
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", textAlign: "center", mt: 1 }}
              >
                {t("Saving your preferences...")}
              </Typography>
            </Box>
          )}

          <ButtonGroup
            fullWidth={isMobile}
            variant="contained"
            sx={{ gap: 1 }}
          >
            <Button
              onClick={handleSaveSettings}
              disabled={isUnchanged || saving}
              startIcon={<SaveIcon />}
              sx={{
                flex: 1,
                py: 1.5,
                borderRadius: 3,
                background: isUnchanged 
                  ? "linear-gradient(45deg, #cccccc 30%, #aaaaaa 90%)"
                  : "linear-gradient(45deg, #4caf50 30%, #45a049 90%)",
                textTransform: "none",
                fontWeight: "bold",
                "&:hover": {
                  background: isUnchanged 
                    ? "linear-gradient(45deg, #cccccc 30%, #aaaaaa 90%)"
                    : "linear-gradient(45deg, #45a049 30%, #4caf50 90%)",
                },
              }}
            >
              {saving ? t("Saving...") : t("Save Changes")}
            </Button>
            
            <Button
              onClick={handleLeaveWithoutSaving}
              startIcon={<CancelIcon />}
              variant="outlined"
              sx={{
                flex: 1,
                py: 1.5,
                borderRadius: 3,
                textTransform: "none",
                fontWeight: "bold",
                borderColor: isUnchanged ? "primary.main" : "error.main",
                color: isUnchanged ? "primary.main" : "error.main",
                "&:hover": {
                  borderColor: isUnchanged ? "primary.dark" : "error.dark",
                  backgroundColor: isUnchanged ? "primary.light" : "error.light",
                },
              }}
            >
              {isUnchanged ? t("Exit") : t("Discard Changes")}
            </Button>
          </ButtonGroup>

          {isUnchanged && (
            <Fade in>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  textAlign: "center",
                  mt: 1,
                  fontSize: "0.75rem",
                  fontStyle: "italic",
                }}
              >
                {t("All settings are up to date")}
              </Typography>
            </Fade>
          )}
        </Paper>
      </Slide>
    </Box>
  );
};

export default SettingsPage;